"use client";

import { useAtomValue, useSetAtom } from "jotai";
import { PannelVisible, ShowItemIndex } from "./Pannel";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronRight, Cpu, Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import RouterLink from "@/components/ui/router-link";
import { DirectusImage } from "@/components/DirectusImage";
import { getLocale } from "@/lib/getSite";
import { useScopedI18n } from "@/messages/client";
import { useCurrentLocale } from "@/hooks/useCurrentLocale";
import { useThrottleFn } from "ahooks";

type NavItemType = {
  title: string;
  href?: string;
  children?: NavItemType[];
} & {
  mode?: "link" | "card";
  icon?: string;
  description?: string;
  subtitle?: string;
  tel?: string;
  vertical?: boolean;
};

function LevelNav(props: { navs: NavItemType[] }) {
  const [hoverIndex, setHoverIndex] = useState(0);

  const setShowIndex = useSetAtom(ShowItemIndex);
  const setShow = useSetAtom(PannelVisible);

  const data = useMemo<NavItemType[] | null>(() => {
    if (!props.navs) return null;
    return props.navs[hoverIndex]?.children ?? null;
  }, [props.navs, hoverIndex]);

  const { run } = useThrottleFn(
    (index: number) => {
      setHoverIndex(index);
    },
    {
      wait: 300,
    }
  );

  useEffect(() => {
    setHoverIndex(0);
  }, [props.navs]);

  return (
    <div className="max-h-[calc(100vh-3.75rem)] overflow-y-auto">
      <div className="content_container relative h-full">
        <div className="flex min-h-[40vh]">
          <div className="xl:-ml-[calc(50vw-600px)] box-content w-40 flex-shrink-0 bg-zinc-100 pl-[calc(50vw-600px)] lg:w-60 xl:w-80">
            {props.navs?.map((item, index) => (
              <RouterLink
                key={index}
                href={item.href}
                onClick={() => {
                  if (item.href) {
                    setShowIndex(null);
                    setShow(false);
                  }
                }}
                className={cn(
                  "relative flex h-14 cursor-pointer items-center justify-between pr-5 text-base text-[#383838] transition-colors duration-300 sm:text-sm lg:h-[4.375rem] lg:text-lg",
                  "xl:-ml-[calc(50vw-600px)] box-content pl-[calc(50vw-600px)] max-xl:pl-2",
                  {
                    "font-bold text-[#FF5100]": hoverIndex === index,
                    "bg-white": hoverIndex === index,
                  }
                )}
                onMouseEnter={() => run(index)}>
                <span>{item.title}</span>

                <div
                  className={cn(
                    "absolute right-0 top-1/2 -translate-y-1/2 opacity-0 transition-opacity duration-300",
                    {
                      "opacity-100": hoverIndex === index,
                    }
                  )}>
                  <ChevronRight color="#383838" size={24} />
                </div>
              </RouterLink>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto bg-white px-6 py-6 lg:px-8 xl:px-12">
            <div className="grid grid-cols-3 gap-x-8 gap-y-6 lg:gap-x-10 lg:gap-y-8 xl:gap-x-14 xl:gap-y-10">
              {data?.map((item, index) => (
                <div key={index}>
                  <RouterLink
                    href={item.href}
                    onClick={() => {
                      if (item.href) {
                        setShowIndex(null);
                        setShow(false);
                      }
                    }}
                    className="select-none border-b-2 border-[#383838] py-2 text-base font-medium text-black lg:text-lg">
                    {item.title}
                  </RouterLink>
                  <div className={cn("mt-4 space-y-2")}>
                    {item.children?.map((itez, indez) => (
                      <RouterLink
                        key={indez}
                        href={itez.href}
                        onClick={() => {
                          if (itez.href) {
                            setShowIndex(null);
                            setShow(false);
                          }
                        }}
                        className="flex select-none items-center space-x-1 text-xs text-[#808080] lg:space-x-2 lg:text-base">
                        <div className="flex-shrink-0">
                          <Cpu size={16} />
                        </div>
                        <span>{itez.title}</span>
                      </RouterLink>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CardNav(props: { navs: NavItemType[] }) {
  const locale = useCurrentLocale();
  const t = useScopedI18n("全局导航条");
  const setShowIndex = useSetAtom(ShowItemIndex);
  const setShow = useSetAtom(PannelVisible);

  return (
    <div className="content_container">
      <div className="grid grid-cols-2 lg:grid-cols-3 pb-10 pt-6 lg:pb-14 lg:pt-10 gap-x-16 gap-y-16">
        {props.navs?.map((item, index) => (
          <div
            className={cn(
              "flex flex-col justify-between text-lg lg:text-xl",
            )}
            key={index}>
            <div className="flex items-center justify-between border-b border-[#A6A6A6] py-3">
              <span>{item.title}</span>

              {item.icon && (
                <DirectusImage
                  id={item.icon}
                  width={32}
                  height={32}
                  alt={item.title}
                />
              )}
            </div>

            <div className="flex-1 py-4 text-sm text-[#808080]">
              {item.description && <span>{item.description}</span>}

              {item.tel && (
                <div className="flex items-center space-x-2">
                  <Phone size={16} />

                  <span>
                    {t("热线电话")}：{item.tel}
                  </span>
                </div>
              )}
            </div>

            <RouterLink
              href={item.href}
              onClick={() => {
                if (item.href) {
                  setShowIndex(null);
                  setShow(false);
                }
              }}
              className="flex select-none items-center space-x-4 text-base text-[#383838]">
              <span>{item.subtitle}</span>

              <ChevronRight color="#FF5100" size={18} />
            </RouterLink>
          </div>
        ))}
      </div>
    </div>
  );
}

function LinkNav(props: { navs: NavItemType[] }) {
  const locale = getLocale();

  const setShowIndex = useSetAtom(ShowItemIndex);
  const setShow = useSetAtom(PannelVisible);

  return (
    <div className="content_container">
      <div className="flex justify-center space-x-5 py-6 lg:space-x-10 lg:py-8">
        {props.navs?.map((item, index) => (
          <RouterLink
            className={cn(
              "flex-1 text-lg text-black transition-colors duration-300 hover:text-[#FF5100] en:text-center lg:text-xl",
              {
                "text-base lg:text-lg": locale === "en",
              }
            )}
            onClick={() => {
              if (item.href) {
                setShow(false);
                setShowIndex(null);

                if (window.location.hash) {
                  window.location.href = item.href;
                }
              }
            }}
            key={index}
            href={item.href}
          >
            {item.title}
          </RouterLink>
        ))}
      </div>
    </div>
  );
}

export function Navigation(props: { navs: NavItemType[] }) {
  const showIndex = useAtomValue(ShowItemIndex);

  const data = useMemo(() => {
    if (showIndex === null) return null;
    return props.navs?.[showIndex]?.children ?? [];
  }, [props.navs, showIndex]);

  const renderNav = useCallback(
    (data: NavItemType[]) => {
      if (data.length === 0 || showIndex === null) return null;

      const item = data[0];

      if (item.mode && item.mode === "link" && item.children?.length) {
        return <LevelNav navs={data} />;
      }

      if (item.mode && item.mode === "card") {
        return <CardNav navs={data} />;
      }

      return <LinkNav navs={data} />;
    },
    [showIndex]
  );

  return (
    <div className={cn("pb-28", { "!pb-0": !data })}>
      <div className="w-full bg-white">{renderNav(data ?? [])}</div>
    </div>
  );
}
